import type { ScopedResult } from 'instantsearch.js';
import { useInstantSearch } from 'react-instantsearch-core';

import { isEyewearEnabled } from '@/utils/eyewear';
import { clearNavigatingPath } from '@/utils/navigationControl';

import type { SearchViewType } from '@/components/Search/types';
import { useRouter } from '@/hooks/useRouter';
import type { UrlOpts } from '@/lib/paths';
import { paths } from '@/lib/paths';

import { getAllTabTotalHits } from '../utils';

import * as M from '../messages';
import * as S from './SearchTabs.styles';

interface SearchTabsWrapperProps {
  activeTab: SearchViewType;
  draftMode?: boolean;
  eyewearInitialNbHits: number;
  filteredResultsReady?: boolean;
  storeInitialNbHits: number;
  strapInitialNbHits: number;
  watchInitialNbHits: number;
}

export const ResultsTabsWrapper = ({
  activeTab,
  draftMode = false,
  eyewearInitialNbHits,
  filteredResultsReady = true,
  storeInitialNbHits,
  strapInitialNbHits,
  watchInitialNbHits,
}: SearchTabsWrapperProps) => {
  const { scopedResults } = useInstantSearch();
  return (
    <ResultsTabs
      activeTab={activeTab}
      data-testid='ResultsTabsWrapper'
      draftMode={draftMode}
      eyewearInitialNbHits={eyewearInitialNbHits}
      filteredResultsReady={filteredResultsReady}
      scopedResults={scopedResults}
      storeInitialNbHits={storeInitialNbHits}
      strapInitialNbHits={strapInitialNbHits}
      watchInitialNbHits={watchInitialNbHits}
    />
  );
};

interface SearchTabsProps extends SearchTabsWrapperProps {
  scopedResults: ScopedResult[];
}

interface ResultsCountProps {
  activeTab: SearchViewType;
  filteredResultsReady?: boolean;
  initialNbHits: number;
  scopedResults: ScopedResult[];
}

const ResultsCount = ({
  activeTab,
  filteredResultsReady = true,
  initialNbHits,
  scopedResults,
}: ResultsCountProps) => {
  if (!filteredResultsReady) {
    return null;
  }

  let count = scopedResults.find((results) => results.indexId === activeTab)
    ?.results?.nbHits;

  if (count == 0 || count == undefined || count == null) {
    count = initialNbHits;
  }

  return M.TranslatedFilter(activeTab, count ?? 0);
};

export const ResultsTabs = ({
  activeTab,
  draftMode = false,
  eyewearInitialNbHits,
  filteredResultsReady = true,
  scopedResults,
  storeInitialNbHits,
  strapInitialNbHits,
  watchInitialNbHits,
}: SearchTabsProps) => {
  const { push, query } = useRouter();
  const inputQuery = query.q ? { q: query.q } : undefined;
  const countAll = getAllTabTotalHits(scopedResults);

  const availableUrls = {
    all: paths.search.all.url(inputQuery as UrlOpts),
    watches: paths.search.watches.url(inputQuery as UrlOpts),
    straps: paths.search.straps.url(inputQuery as UrlOpts),
    stores: paths.search.stores.url(inputQuery as UrlOpts),
    eyewear: paths.search.eyewear.url(inputQuery as UrlOpts),
  };

  const handleClick = (evt: React.MouseEvent, path: SearchViewType) => {
    evt.preventDefault();

    // Clear any previous navigating path to ensure new navigation works
    clearNavigatingPath();

    delete query.urlParts;
    push(availableUrls[path], undefined, { shallow: true });
  };

  const createTab = (path: SearchViewType, children: React.ReactNode) => (
    <S.Tab
      $isActive={activeTab === path}
      data-testid={`ResultsTabsTab-${path}`}
      href={availableUrls[path]}
      onClick={(e) => handleClick(e, path)}
    >
      {children}
    </S.Tab>
  );

  return (
    <S.Tabs data-testid='ResultsTabs'>
      {createTab(
        'all',
        filteredResultsReady
          ? M.YourResults(
              countAll == 0
                ? watchInitialNbHits +
                    strapInitialNbHits +
                    storeInitialNbHits +
                    eyewearInitialNbHits
                : countAll,
            )
          : null,
      )}
      {createTab(
        'watches',
        <ResultsCount
          activeTab='watches'
          data-testid='ResultsTabsTab-watches'
          filteredResultsReady={filteredResultsReady}
          initialNbHits={watchInitialNbHits}
          scopedResults={scopedResults}
        />,
      )}
      {createTab(
        'straps',
        <ResultsCount
          activeTab='straps'
          data-testid='ResultsTabsTab-straps'
          filteredResultsReady={filteredResultsReady}
          initialNbHits={strapInitialNbHits}
          scopedResults={scopedResults}
        />,
      )}
      {isEyewearEnabled(draftMode) &&
        createTab(
          'eyewear',
          <ResultsCount
            activeTab='eyewear'
            data-testid='ResultsTabsTab-eyewear'
            filteredResultsReady={filteredResultsReady}
            initialNbHits={eyewearInitialNbHits}
            scopedResults={scopedResults}
          />,
        )}
      {createTab(
        'stores',
        <ResultsCount
          activeTab='stores'
          data-testid='ResultsTabsTab-stores'
          filteredResultsReady={filteredResultsReady}
          initialNbHits={storeInitialNbHits}
          scopedResults={scopedResults}
        />,
      )}
    </S.Tabs>
  );
};
